import { RootState } from "app/store";
import Menu from "components/common/Menu";
import { SideBarTabs } from "constants/tabs";
import { useTabActive } from "hook/useTabActive";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CommonStyles } from "styles/CommonStyles";
import { CarType } from "types/loadedData";
import { IoChevronDown } from "react-icons/io5";
import CarCard from "components/card/CarCard";
import AppCopy from "copy/AppCopy";
import { useNavigate, useParams } from "react-router-dom";
import useUser from "hook/useUser";
import { formatDate } from "functions/text";
import useCars from "hook/useCars";
import useChat from "hook/useChat";
import { Paths } from "router/Paths";
import {
  dashboardConfirm,
  translateDashboardText,
} from "i18n/dashboardLanguage";

function UserPage() {
  const params = useParams();
  const {
    getUser,
    suspendUser,
    unsuspendUser,
    userSuspension,
    updateUserType,
    updateUser,
  } = useUser();
  const { sendDirectMessage } = useChat();
  const { getUserCars } = useCars();
  const navigate = useNavigate();
  const { cars } = useSelector((state: RootState) => state.car);
  const { customer } = useSelector((state: RootState) => state.user);
  const [loadedCars, setLoadedCars] = useState<CarType[]>(cars);
  const [count, setCount] = useState<number>(0);
  const [radio, setRadio] = useState<number>();
  const [loadingMore, setloadingMore] = useState<boolean>(false);
  const [editBalance, setEditBalance] = useState<boolean>(false);
  const [balance, setBalance] = useState<string | number>(
    customer?.balance || "0"
  );
  const [messageModalOpen, setMessageModalOpen] = useState<boolean>(false);
  const [messageText, setMessageText] = useState<string>("");
  const [sendingMessage, setSendingMessage] = useState<boolean>(false);
  const [messageError, setMessageError] = useState<string>("");
  const [sentConversationId, setSentConversationId] = useState<string>("");

  const selectedCustomerId = params.id || customer?._id || "";
  const activeCustomer =
    customer?._id && String(customer._id) === String(selectedCustomerId)
      ? customer
      : null;

  useTabActive(SideBarTabs.Users.name);

  const loadData = async () => {
    if (!params.id) return;
    setEditBalance(false);
    setMessageModalOpen(false);
    setMessageError("");
    setMessageText("");
    setSentConversationId("");
    setCount(0);
    setLoadedCars([]);
    await getUser({ id: params.id });
    const { cars, count: loadedCount } = await getUserCars({ id: params.id });
    setLoadedCars(cars);
    setCount(loadedCount);
  };

  useEffect(() => {
    void loadData();
    setBalance("0");
  }, [params.id]);

  useEffect(() => {
    setBalance(String(activeCustomer?.balance || "0"));
  }, [activeCustomer?._id, activeCustomer?.balance]);

  const isUserSuspended = (): boolean => {
    let status = false;
    const currentDate = new Date();
    if (activeCustomer?.isActive === false) {
      status = true;
    }
    if (activeCustomer?.postingPrivilageSuspension) {
      const suspensionDate = new Date(
        activeCustomer.postingPrivilageSuspension
      );
      if (suspensionDate.getTime() > currentDate.getTime()) {
        status = true;
      }
    }
    return status;
  };

  const handleLoadMore = async () => {
    if (!params.id) return;
    const { cars } = await getUserCars({
      id: params.id,
      setLoading: setloadingMore,
      offset: loadedCars.length,
    });
    if (cars) setLoadedCars([...loadedCars, ...cars]);
  };

  const handleSuspend = async () => {
    if (!params.id) return;
    if (!radio && !isUserSuspended())
      return window.alert(
        translateDashboardText("Please select the duration of suspension")
      );
    if (isUserSuspended()) {
      if (activeCustomer?.isActive === false) {
        await userSuspension({ id: params.id, isActive: true });
      } else {
        await unsuspendUser({ user: params.id });
      }
    } else {
      let date = "";

      const currentDate = new Date();

      if (radio === 1) {
        currentDate.setDate(currentDate.getDate() + 1);
      } else if (radio === 2) {
        currentDate.setDate(currentDate.getDate() + 3);
      } else if (radio === 3) {
        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      date = currentDate.toISOString();

      if (radio === 4) {
        await userSuspension({ id: params.id, isActive: false });
      } else {
        await suspendUser({ user: params.id, date });
      }
    }
  };

  const handleUserType = async (type: string) => {
    const confirmation = dashboardConfirm(
      `Are you sure you want to change this customer into a ${type}?`
    );

    if (confirmation) {
      if (!selectedCustomerId) return;
      await updateUserType({
        type,
        id: selectedCustomerId,
      });
    }
  };

  const handleUpdateBalance = async () => {
    if (!selectedCustomerId) return;
    setEditBalance(false);
    await updateUser({
      id: selectedCustomerId,
      payload: {
        balance: balance || 0,
      },
    });
  };

  const openMessageComposer = () => {
    setMessageModalOpen(true);
    setMessageError("");
    setMessageText("");
    setSentConversationId("");
  };

  const closeMessageComposer = () => {
    setMessageModalOpen(false);
    setMessageError("");
    setMessageText("");
    setSentConversationId("");
  };

  const handleSendDirectMessage = async () => {
    if (!selectedCustomerId) return;

    const normalizedMessage = messageText.trim();
    if (!normalizedMessage) {
      setMessageError("Message text is required");
      return;
    }

    setSendingMessage(true);
    setMessageError("");
    try {
      const conversation = await sendDirectMessage({
        customerId: selectedCustomerId,
        text: normalizedMessage,
      });

      if (!conversation?._id) {
        setMessageError("Failed to send message");
        return;
      }

      setSentConversationId(String(conversation._id));
      setMessageText("");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleOpenThread = () => {
    if (!sentConversationId) return;

    closeMessageComposer();
    navigate(Paths.messages, {
      state: { conversationId: sentConversationId },
    });
  };

  const onRadioSelect = (index: number) => {
    setRadio(index);
  };

  const {
    permanentText,
    postedCarsText,
    messageButtonText,
    messageComposerCancelText,
    messageComposerNoteText,
    messageComposerOpenThreadText,
    messageComposerPlaceholderText,
    messageComposerSendText,
    messageComposerSuccessText,
    messageComposerTitleText,
    suspendButtonText,
    unSuspendButtonText,
    titleText,
    idText,
  } = AppCopy.userPage;

  const Radio = ({ tag, text }: { tag: number; text: string }) => {
    return (
      <div
        className={ClassName.radioContainer}
        onClick={() => onRadioSelect(tag)}
      >
        <div
          className={`${ClassName.radio} ${
            radio === tag ? ClassName.radioActive : ClassName.radioInActive
          }`}
        ></div>
        <span
          className={`${ClassName.radioText} ${
            radio === tag
              ? ClassName.radioTextActive
              : ClassName.radioTextInActive
          }`}
        >
          {text}
        </span>
      </div>
    );
  };

  return (
    <div className={`${CommonStyles.pageWrapper} bg-white`}>
      <Menu />
      <div className={ClassName.container}>
        <div className={ClassName.header}>
          <div className={ClassName.headerContent}>
            <div className={ClassName.headerTopRow}>
              <span className={ClassName.title}>{titleText}</span>
              <button
                type="button"
                className={ClassName.messageButton}
                onClick={openMessageComposer}
                disabled={!selectedCustomerId}
              >
                {messageButtonText}
              </button>
            </div>
            <span className={ClassName.id}>
              {idText} : {activeCustomer?._id || selectedCustomerId}
            </span>
            <span className={ClassName.name}>
              {activeCustomer?.name || "Loading..."}
            </span>
            <span>{activeCustomer?.phoneNumber || "-"}</span>
            <span>{activeCustomer?.email || "-"}</span>
            {!editBalance && (
              <div
                className="flex gap-2 items-center cursor-pointer justify-between w-full my-4 font-bold"
                onClick={() => setEditBalance(true)}
              >
                <span>Balance: {activeCustomer?.balance || balance}</span>
                <img
                  src="/IconEdit.svg"
                  alt=""
                  className="w-4 object-contain"
                />
              </div>
            )}
            {editBalance && (
              <div className="flex flex-col gap-2">
                <input
                  type="text"
                  className="w-full h-10 rounded-md border px-3"
                  value={balance}
                  onChange={(e) => {
                    const numericValue = e.target?.value?.replace(
                      /[^0-9]/g,
                      ""
                    );
                    setBalance(numericValue);
                  }}
                />
                <button
                  className="px-5 py-2 rounded-md bg-blue text-white font-bold text-sm"
                  onClick={handleUpdateBalance}
                >
                  Update
                </button>
              </div>
            )}
          </div>
          <div className={ClassName.suspendContent}>
            <button className={ClassName.button} onClick={handleSuspend}>
              {isUserSuspended() ? unSuspendButtonText : suspendButtonText}
            </button>
            {!isUserSuspended() ? (
              <>
                <Radio tag={1} text="1 day" />
                <Radio tag={2} text="3 days" />
                <Radio tag={3} text="1 month" />
                <Radio tag={4} text={permanentText} />
              </>
            ) : (
              <span>
                {activeCustomer?.isActive === false
                  ? "User is suspended"
                  : `User is suspended until ${formatDate(
                      activeCustomer?.postingPrivilageSuspension
                    )}`}
              </span>
            )}
          </div>
          <div className={ClassName.suspendContent}>
            <span className="mb-2">Permanent</span>

            <div className="flex gap-4">
              <button
                className={
                  activeCustomer?.userType === "buyer"
                    ? ClassName.button
                    : ClassName.button1
                }
                onClick={() => handleUserType("buyer")}
              >
                Buyer
              </button>
              <button
                className={
                  activeCustomer?.userType === "seller"
                    ? ClassName.button
                    : ClassName.button1
                }
                onClick={() => handleUserType("seller")}
              >
                Seller
              </button>
            </div>
          </div>
        </div>
        <span className={ClassName.postedCars}>
          {count} {postedCarsText}
        </span>

        {loadedCars.map((car, index) => (
          <CarCard key={index} car={car} />
        ))}

        {loadedCars && count > loadedCars.length && !loadingMore && (
          <div className={ClassName.loadModeButtonContainer}>
            <div className={ClassName.loadModeButton} onClick={handleLoadMore}>
              <IoChevronDown size={20} />
            </div>
          </div>
        )}
      </div>

      {messageModalOpen && (
        <div className={ClassName.modalOverlay} onClick={closeMessageComposer}>
          <div
            className={ClassName.modalCard}
            onClick={(event) => event.stopPropagation()}
          >
            {!sentConversationId ? (
              <>
                <div className={ClassName.modalHeader}>
                  <span className={ClassName.modalTitle}>
                    {messageComposerTitleText}
                  </span>
                  <button
                    type="button"
                    className={ClassName.modalCloseButton}
                    onClick={closeMessageComposer}
                  >
                    ×
                  </button>
                </div>
                <span className={ClassName.modalNote}>
                  {messageComposerNoteText}
                </span>
                <div className={ClassName.modalUserMeta}>
                  <span className={ClassName.modalUserName}>
                    {activeCustomer?.name || "User"}
                  </span>
                  <span className={ClassName.modalUserPhone}>
                    {activeCustomer?.phoneNumber || selectedCustomerId}
                  </span>
                </div>
                <textarea
                  className={ClassName.messageTextarea}
                  placeholder={messageComposerPlaceholderText}
                  value={messageText}
                  onChange={(event) => setMessageText(event.target.value)}
                  rows={6}
                />
                {messageError && (
                  <span className={ClassName.modalError}>{messageError}</span>
                )}
                <div className={ClassName.modalActions}>
                  <button
                    type="button"
                    className={ClassName.cancelButton}
                    onClick={closeMessageComposer}
                    disabled={sendingMessage}
                  >
                    {messageComposerCancelText}
                  </button>
                  <button
                    type="button"
                    className={ClassName.messageButton}
                    onClick={handleSendDirectMessage}
                    disabled={sendingMessage}
                  >
                    {sendingMessage ? (
                      <div className="buttonLoader"></div>
                    ) : (
                      <span>{messageComposerSendText}</span>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={ClassName.modalHeader}>
                  <span className={ClassName.modalTitle}>
                    {messageComposerSuccessText}
                  </span>
                  <button
                    type="button"
                    className={ClassName.modalCloseButton}
                    onClick={closeMessageComposer}
                  >
                    ×
                  </button>
                </div>
                <span className={ClassName.modalNote}>
                  The conversation is ready. Open the thread to continue the
                  chat.
                </span>
                <div className={ClassName.modalActions}>
                  <button
                    type="button"
                    className={ClassName.cancelButton}
                    onClick={closeMessageComposer}
                  >
                    {messageComposerCancelText}
                  </button>
                  <button
                    type="button"
                    className={ClassName.messageButton}
                    onClick={handleOpenThread}
                  >
                    {messageComposerOpenThreadText}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default UserPage;

const ClassName = {
  container:
    "flex min-w-0 flex-col rounded-2xl border border-slate-200 bg-white p-4 sm:p-5",
  loadModeButtonContainer: "w-full flex items-center justify-center",
  loadModeButton: "p-4 cursor-pointer",

  header: "flex flex-col gap-6 text-primaryText text-base lg:flex-row",
  headerContent: "flex flex-col pt-4",
  headerTopRow: "flex items-start justify-between gap-4",
  title: "font-bold",
  id: "font-bold text-sm my-2",
  name: "font-bold my-2",
  suspendContent: "flex flex-col",
  button:
    "cursor-pointer h-11 bg-primary px-4 rounded-md flex items-center justify-center text-white text-base font-semibold mb-4",
  button1:
    "cursor-pointer h-11 bg-[#D9D9D9] px-4 rounded-md flex items-center justify-center text-black text-base font-semibold mb-4",
  radioContainer: "flex items-center gap-4 cursor-pointer my-2",
  radio: "w-4 h-4 rounded-full",
  radioText: "text-sm",

  radioActive: "bg-primary",
  radioInActive: "bg-gray-400",
  radioTextActive: "text-primary",
  radioTextInActive: "text-PrimaryText",

  postedCars: "my-4 text-secondaryText text-sm w-full",
  messageButton:
    "cursor-pointer h-11 bg-primary px-4 rounded-md flex items-center justify-center text-white text-base font-semibold disabled:opacity-60 disabled:cursor-not-allowed",
  cancelButton:
    "cursor-pointer h-11 bg-[#D9D9D9] px-4 rounded-md flex items-center justify-center text-black text-base font-semibold disabled:opacity-60 disabled:cursor-not-allowed",
  modalOverlay:
    "fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4",
  modalCard:
    "w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl flex flex-col gap-4",
  modalHeader: "flex items-center justify-between gap-4",
  modalTitle: "text-lg font-semibold text-primaryText",
  modalCloseButton:
    "w-9 h-9 rounded-full border border-slate-200 text-secondaryText hover:bg-slate-100 transition-colors",
  modalNote: "text-sm leading-6 text-secondaryText",
  modalUserMeta:
    "flex flex-col gap-1 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3",
  modalUserName: "text-sm font-semibold text-primaryText",
  modalUserPhone: "text-xs text-secondaryText",
  messageTextarea:
    "w-full min-h-32 rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none resize-none focus:border-primary",
  modalError: "text-xs text-red-600",
  modalActions: "flex items-center justify-end gap-3 pt-2",
};
